<template>
  <NuxtLayout header-height="8.5rem" name="sticky">
    <template #header>
      <HighFrequencyGameHeader with-fly-out @click="navigateBack">
        <HighFrequencyGamePrizeBanner @click="isPrizeModalVisible = true" />
      </HighFrequencyGameHeader>
    </template>

    <LcCard
      body-class="flex flex-col py-150 px-100 gap-200"
      class="container mt-200"
      header-class="flex justify-between px-100"
    >
      <template #header>
        <LcLotteryRemainingPicks
          :complete="remaining <= 0"
          :done-label="$t('highfrequencygame.spots.remaining.empty')"
          v-slot="{ countClass }"
        >
          <i18n-t keypath="highfrequencygame.spots.remaining" :n="2" scope="global">
            <template #count>
              <span :class="countClass">{{ remaining }}</span>
            </template>
          </i18n-t>
        </LcLotteryRemainingPicks>

        <HighFrequencyGameSpotCount v-if="numberOfSpots" v-bind="{ numberOfSpots }" />
      </template>

      <template v-if="numbers">
        <LcLotteryNumberSelector
          v-for="field in fieldConfig"
          :key="field.name"
          :label="$t('highfrequencygame.spots.selector.label')"
          v-bind="{
            ...numbers,
            value: getSelectedNumbers(field),
          }"
          :style="theme"
          variant="high-frequency"
          @update:value="(numbers) => setNumbers(field.name, numbers)"
        />
      </template>

      <HighFrequencyGameAddOnToggle
        v-for="{ name } in addOns"
        v-bind="{ name }"
        :key="name"
        :disabled="disabledAddOns.includes(name)"
        :model-value="selectedAddOns.includes(name)"
        @update:model-value="(value) => setAddOn(name, value)"
      />

      <HighFrequencyGameChargeSelector
        v-bind="{ wagers }"
        :model-value="selectedWager"
        @update:model-value="setWager"
      />

      <HighFrequencyGameQuantitySelector
        v-bind="allowedNumberOfDraws"
        :model-value="selectedQuantity"
        @update:model-value="(value) => setQuantity(value)"
      />
    </LcCard>

    <div class="flex justify-center mt-300 mb-200">
      <LcButton
        :disabled="!hasSelectedNumbers"
        icon="trash-full"
        size="medium"
        variant="secondary"
        @click="handleReset"
      >
        {{ $t('highfrequencygame.clear') }}
      </LcButton>
    </div>

    <TheMetaLinks hide-prizes no-margin-bottom />

    <ClientOnly>
      <HighFrequencyGameCompliance class="mt-100" />
    </ClientOnly>

    <FloatingQuickPickButton @click="isQuickPickModalVisible = true" />

    <!-- CAN NOT BE LAZY, will break styling of SSR -->
    <HighFrequencyGamePrizeModal v-if="isPrizeModalVisible" @close="isPrizeModalVisible = false" />

    <LazyHighFrequencyGameQuickPickModal
      v-if="isQuickPickModalVisible"
      @close="isQuickPickModalVisible = false"
    />

    <LazyHighFrequencyGameTutorialModal v-if="showTutorial" @close="onTutorialCompleted" />

    <template #navbar>
      <HighFrequencyGameNavbar ref="navbar" @use-quickpick="isQuickPickModalVisible = true" />
    </template>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { addYears } from 'date-fns'

import {
  type HighFrequency_NumbersConfig,
  KnownProductType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import { COOKIE_HIGH_FREQUENCY_GAME_TUTORIAL } from '~/constants/cookies'
import { QUERY_REVALIDATE } from '~/constants/queryParams'

definePageMeta({
  isGameRoute: true,
  layout: false,
  middleware: [
    'age-verification',
    'self-excluded',
    'high-frequency-game-active',
    'control-high-frequency-game-config-fetch',
  ],
  validate: ({ params: { gameId } }) => {
    const isEnabled = useRuntimeConfig().public.feature.keno
    return isEnabled && useHighFrequencyGamesOfLicensedTerritory().doesExist(String(gameId))
  },
})

const route = useRoute()

const { navigateBack } = useRouterUtils()
const isQuickPickModalVisible = ref(false)
const isPrizeModalVisible = ref(false)
const showTutorial = ref(false)
const tutorialVisited = useCookie(COOKIE_HIGH_FREQUENCY_GAME_TUTORIAL, {
  default: () => false,
  expires: new Date(addYears(new Date(), 1)),
})

const { scrollTop } = useScrollUtils()

const {
  config,
  disabledAddOns,
  disclaimerAcceptedConfig,
  gameId,
  isQuickPick,
  numberOfSpots,
  playSlipValid,
  remaining,
  reset,
  selectedAddOns,
  selectedNumbers,
  selectedQuantity,
  selectedWager,
  setAddOn,
  setNumbers,
  setQuantity,
  setWager,
  wagers,
} = useHighFrequencyGame()

const { removeQueryParams } = useRouterUtils()
const { isLoggedIn } = useAuth()

const addOns = computed(() => config.value?.addOns ?? [])

const allowedNumberOfDraws = computed(() => config.value?.allowedNumberOfDraws ?? [])

const numbers = computed(() => config.value?.numbers[0] || null)
const fieldConfig = computed(() => config.value?.numbers ?? [])

const getSelectedNumbers = (field: HighFrequency_NumbersConfig) =>
  selectedNumbers.value.find(({ name }) => name === field.name)?.numbers ?? []

const onTutorialCompleted = () => {
  showTutorial.value = false
  tutorialVisited.value = true
}

const { createCssVar } = getLotteryCssVarConstructor(useLicensedTerritory(), gameId)

const theme = computed(() => ({
  '--hfg-number-checked-background': createCssVar('high-frequency-ball-selected-background'),
  '--hfg-number-checked-border-color': createCssVar('high-frequency-ball-selected-border-color'),
  '--hfg-number-checked-color': createCssVar('high-frequency-ball-selected-text-color'),
}))

const navbar = useTemplateRef('navbar')

const { pushRemoveFromCartEvent, pushViewItemEvent } = useTracking()
const { createECommerceEventData } = useTrackingData()
const { logTrackingError } = useTrackingErrorLog()

const { currentPrice } = useHighFrequencyGame()

const generateEventData = async () => {
  if (!config.value) return null
  return await createECommerceEventData({
    addOns: selectedAddOns.value,
    currentPrice: currentPrice.value,
    gameId: config.value.gameId,
    isQuickPick: isQuickPick.value,
    numberOfGames: selectedQuantity.value ?? 0,
    type: KnownProductType.HighFrequencyGame,
    wager:
      wagers.value.find((wager) => wager.name === selectedWager.value)?.price.amount.value ?? 0,
  })
}

const hasTrackedViewItemEvent = ref(false)

const hasSelectedNumbers = computed(() =>
  selectedNumbers.value.some(({ numbers }) => numbers.length > 0),
)

const trackViewItemEvent = async () => {
  if (!config.value || hasTrackedViewItemEvent.value) return
  try {
    pushViewItemEvent(await generateEventData())
  } catch (err) {
    logTrackingError('view_item', err)
  }
  hasTrackedViewItemEvent.value = true
}

const trackRemoveFromCartEvent = async () => {
  try {
    pushRemoveFromCartEvent(await generateEventData())
  } catch (err) {
    logTrackingError('remove_from_cart', err)
  }
}

const handleReset = () => {
  reset()
  scrollTop()
}

onMounted(trackViewItemEvent)
watch(config, trackViewItemEvent)
watch(playSlipValid, (isValid) => {
  if (!isValid) {
    trackRemoveFromCartEvent()
  }
})

onMounted(async () => {
  if (!tutorialVisited.value && !disclaimerAcceptedConfig.value) {
    showTutorial.value = true
  }

  if (route.query[QUERY_REVALIDATE]) {
    await removeQueryParams(QUERY_REVALIDATE)

    if (!isLoggedIn.value) return
    return await navbar.value?.redirectToCheckout()
  }
})
</script>

<style lang="scss" scoped>
.container {
  container-type: inline-size;
}
</style>
